import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@app/context/auth'
import { useKnowledgeHubUrl } from '@app/hooks/useKnowledgeHubUrl'
import { useResourceAreas } from '@app/modules/resources/contexts/resource-areas-context'

import { StyledNavLink } from '../StyledNavLink'

interface INavLinksProps {
  shownOnDrawer?: boolean
}

export const NavLinks: FC<INavLinksProps> = () => {
  const { t } = useTranslation()
  const { acl } = useAuth()

  const { hasAnyResourceAccess } = useResourceAreas()
  const knowledgeHubRedirectEnabled = useFeatureFlagEnabled(
    'knowledge-hub-redirect',
  )
  const knowledgeHubUrl = useKnowledgeHubUrl()

  const resourcesUrl = useMemo(() => {
    if (!knowledgeHubRedirectEnabled && !hasAnyResourceAccess) {
      return ''
    }

    if (knowledgeHubRedirectEnabled) {
      return acl.isAustralia()
        ? import.meta.env.VITE_ANZ_KNOWLEDGE_HUB_RESOURCES_URL
        : import.meta.env.VITE_UK_KNOWLEDGE_HUB_RESOURCES_URL
    }

    return '/resources'
  }, [acl, hasAnyResourceAccess, knowledgeHubRedirectEnabled])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: { xs: 'center', lg: 'flex-start' },
      }}
    >
      <Link
        component={StyledNavLink}
        to={'/'}
        sx={{ px: 2, textAlign: 'center' }}
      >
        {t('home')}
      </Link>

      {resourcesUrl ? (
        <Link
          component={StyledNavLink}
          to={resourcesUrl}
          sx={{ px: 2, textAlign: 'center' }}
        >
          {t('resources')}
        </Link>
      ) : null}

      {acl.canParticipateInCourses() ? (
        <Link
          component={StyledNavLink}
          to="/courses"
          sx={{ px: 2, textAlign: 'center' }}
        >
          {t('my-courses')}
        </Link>
      ) : null}

      {acl.canManageOrgCourses() ||
      acl.isBookingContact() ||
      acl.isOrgKeyContact() ? (
        <Link
          component={StyledNavLink}
          to="/manage-courses"
          sx={{ px: 2, textAlign: 'center' }}
        >
          {t('manage-courses')}
        </Link>
      ) : null}
      {acl.canPurchaseCourseMaterials() ? (
        <Link
          component={StyledNavLink}
          to="/purchase-course-materials"
          sx={{ px: 2, textAlign: 'center' }}
        >
          {t('purchase-course-materials')}
        </Link>
      ) : null}
      {acl.canViewOrganizations() ? (
        <Link
          component={StyledNavLink}
          to="/organisations"
          sx={{ px: 2, textAlign: 'center' }}
        >
          {t('organizations')}
        </Link>
      ) : null}

      {acl.canViewUsers() ? (
        <Link
          component={StyledNavLink}
          to="/admin/users"
          sx={{ px: 2, textAlign: 'center' }}
        >
          {t('users')}
        </Link>
      ) : null}

      {acl.canViewCertifications() && (
        <Link
          component={StyledNavLink}
          to="/certifications"
          sx={{ px: 2, textAlign: 'center' }}
        >
          {t('common.certifications')}
        </Link>
      )}
      {acl.canViewOrders() && (
        <Link
          component={StyledNavLink}
          to="/orders"
          sx={{ px: 2, textAlign: 'center' }}
        >
          {t('common.orders')}
        </Link>
      )}

      {!acl.isInternalUser() ? (
        <>
          <Link component={StyledNavLink} to={knowledgeHubUrl}>
            {t('common.knowledge-hub')}
          </Link>

          <Link
            component={StyledNavLink}
            to={
              acl.isAustralia()
                ? import.meta.env.VITE_EVENTS_URL_ANZ
                : import.meta.env.VITE_EVENTS_URL
            }
          >
            {t('common.events')}
          </Link>

          <Link
            component={StyledNavLink}
            to={
              acl.isAustralia()
                ? import.meta.env.VITE_SUPPORT_URL_ANZ
                : import.meta.env.VITE_SUPPORT_URL
            }
          >
            {t('common.support')}
          </Link>
        </>
      ) : null}
    </Box>
  )
}
